/**
 * A section folded into one row (user, 19/10/2026: "one button — clicking
 * it expands; super intuitive and smooth"): the whole row is the toggle —
 * icon, title, a count, a one-line summary that fades out when open, and a
 * Show / Hide pill with a turning chevron that reads as a control. Actions
 * that belong to the section (edit, settings) sit at the row's end, outside
 * the toggle, as compact buttons. "The day", "Lists in use today", the
 * Gateways tab's sections and the warnings all share it.
 * For Media Devoted | Created By Ciro Autiero
 */
import { FC, ReactNode } from 'react';
import { ChevronDown, type LucideIcon } from 'lucide-react';

import { cn } from '@/lib/utils';

/** The Show / Hide control at the end of a toggle row. */
export const TogglePill: FC<{ open: boolean; className?: string }> = ({ open, className }) => (
  <span
    className={cn(
      'inline-flex shrink-0 items-center gap-1 rounded-md border border-border/70 bg-background px-2 py-0.5 text-[11px] font-medium text-muted-foreground transition-colors group-hover:border-primary/50 group-hover:text-primary',
      className,
    )}
    aria-hidden
  >
    {open ? 'Hide' : 'Show'}
    <ChevronDown className={cn('h-3.5 w-3.5 transition-transform duration-300', open && 'rotate-180')} />
  </span>
);

interface Props {
  icon: LucideIcon;
  title: string;
  badge?: ReactNode;
  summary: string;
  open: boolean;
  onToggle: () => void;
  /** Compact buttons at the row's end — outside the toggle, so a click on them never folds the section. */
  actions?: ReactNode;
}

export const SectionToggle: FC<Props> = ({ icon: Icon, title, badge, summary, open, onToggle, actions }) => (
  <div className="flex items-center gap-3 px-5 py-2">
    <button
      type="button"
      aria-expanded={open}
      onClick={onToggle}
      className="group -mx-2 flex min-w-0 flex-1 items-center gap-2 rounded-md px-2 py-1.5 text-left transition-colors hover:bg-muted/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
    >
      <Icon className="h-4 w-4 shrink-0 text-primary" aria-hidden />
      <span className="text-sm font-semibold">{title}</span>
      {badge !== undefined ? (
        typeof badge === 'string' || typeof badge === 'number' ? (
          <span className="rounded-full bg-primary/10 px-2 py-0.5 font-mono text-[11px] font-semibold tabular-nums text-primary">{badge}</span>
        ) : (
          badge
        )
      ) : null}
      <span className={cn('min-w-0 flex-1 truncate text-[11.5px] text-muted-foreground transition-opacity duration-300', open && 'opacity-0')}>{summary}</span>
      <TogglePill open={open} />
    </button>
    {actions ? <div className="flex shrink-0 items-center gap-1.5">{actions}</div> : null}
  </div>
);
